import { useQuery } from '@tanstack/react-query';
import { billing } from '@/api/billing';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import {
  Loader2, CheckCircle2, AlertTriangle, XCircle, Clock, CreditCard,
  BarChart3, Zap, HardDrive, Hash, FileText,
} from 'lucide-react';
import { cn } from '@/lib/cn';

// ── Billing status badge ──────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; icon: React.ElementType; className: string }> = {
  active:     { label: 'Active',    icon: CheckCircle2,    className: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-900' },
  past_due:   { label: 'Past due',  icon: AlertTriangle,   className: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-900' },
  canceled:   { label: 'Canceled',  icon: XCircle,         className: 'bg-muted text-muted-foreground border-border' },
  trialing:   { label: 'Trial',     icon: Clock,           className: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-900' },
  incomplete: { label: 'Incomplete',icon: Clock,           className: 'bg-muted text-muted-foreground border-border' },
  none:       { label: 'No plan',   icon: XCircle,         className: 'bg-muted text-muted-foreground border-border' },
};

function BillingStatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.none!;
  const Icon = cfg.icon;
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border', cfg.className)}>
      <Icon className="h-3.5 w-3.5" />
      {cfg.label}
    </span>
  );
}

// ── Usage progress bar ─────────────────────────────────────────────────────────

function UsageBar({
  label, icon: Icon, quantity, included, unit,
}: {
  label: string;
  icon: React.ElementType;
  quantity: number;
  included: number;
  unit: string;
}) {
  const pct = included > 0 ? Math.min((quantity / included) * 100, 100) : 0;
  const over = quantity > included && included > 0;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-1.5 text-muted-foreground font-medium">
          <Icon className="h-3.5 w-3.5" />
          {label}
        </span>
        <span className={cn('tabular-nums font-semibold', over ? 'text-amber-600 dark:text-amber-400' : 'text-foreground')}>
          {quantity.toFixed(unit === 'GB' ? 2 : 0)} / {included} {unit}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500',
            over ? 'bg-amber-500' : pct > 75 ? 'bg-yellow-500' : 'bg-primary',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      {over && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          {(quantity - included).toFixed(unit === 'GB' ? 2 : 0)} {unit} over limit
        </p>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

const METER_CONFIG: Record<string, { label: string; icon: React.ElementType; unit: string }> = {
  inbound_minutes:  { label: 'Inbound minutes',  icon: Zap,       unit: 'min' },
  outbound_minutes: { label: 'Outbound minutes', icon: Zap,       unit: 'min' },
  storage_gb:       { label: 'Storage',          icon: HardDrive, unit: 'GB'  },
  active_dids:      { label: 'Active DIDs',      icon: Hash,      unit: ''    },
};

export function BillingDashboard() {
  const user = useCurrentUser();

  const { data: summary, isLoading, error } = useQuery({
    queryKey: ['billing-summary'],
    queryFn: () => billing.getSummary(),
    enabled: !!user,
  });

  const { data: paymentMethods } = useQuery({
    queryKey: ['billing-payment-methods'],
    queryFn: () => billing.listPaymentMethods(),
    enabled: !!user && summary?.billing_status !== 'none',
  });

  const card = paymentMethods?.rows?.[0] ?? null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !summary) {
    return (
      <>
        <PageHeader eyebrow="Billing" title="Billing" />
        <div className="rounded-lg border border-dashed p-12 text-center">
          <p className="text-muted-foreground text-sm">No billing information available.</p>
          <p className="text-xs text-muted-foreground/60 mt-1">Contact your Ivrello administrator to set up a subscription plan.</p>
        </div>
      </>
    );
  }

  const totalOverageCents = summary.usage.reduce((s, u) => s + u.overage_cost_cents, 0);

  return (
    <>
      <PageHeader
        eyebrow="Billing"
        title="Billing"
        description="Your current plan, usage, and payment details."
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/billing/custom-invoices">
                <FileText className="h-4 w-4 mr-1.5" />
                Customer invoices
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/billing/invoices">
                <FileText className="h-4 w-4 mr-1.5" />
                Invoices
              </Link>
            </Button>
          </div>
        }
      />

      <div className="space-y-5 max-w-3xl">
        {/* Plan summary */}
        <Card>
          <CardContent className="pt-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2.5 mb-1">
                  <span className="text-xl font-semibold">
                    {summary.plan?.name ?? 'No plan'}
                  </span>
                  <BillingStatusBadge status={summary.billing_status} />
                </div>
                {summary.plan && (
                  <p className="text-2xl font-bold tabular-nums mt-1">
                    ${(summary.plan.monthly_price_cents / 100).toFixed(0)}
                    <span className="text-sm font-normal text-muted-foreground ml-1">/ month</span>
                  </p>
                )}
              </div>
              {summary.billing_status === 'past_due' && (
                <div className="rounded-md border border-amber-200 bg-amber-50/60 px-4 py-3 text-sm text-amber-700 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300 max-w-xs">
                  <AlertTriangle className="inline h-4 w-4 mr-1.5 -mt-0.5" />
                  Payment is past due. Please update your payment method.
                </div>
              )}
              {summary.billing_status === 'none' && (
                <div className="text-sm text-muted-foreground">
                  Contact your administrator to assign a plan.
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Usage meters */}
        {summary.usage.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
                Current period usage
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 pt-2">
              {summary.usage.map((u) => {
                const cfg = METER_CONFIG[u.meter];
                if (!cfg) return null;
                return (
                  <UsageBar
                    key={u.meter}
                    label={cfg.label}
                    icon={cfg.icon}
                    quantity={u.quantity}
                    included={u.included}
                    unit={cfg.unit}
                  />
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Estimated invoice */}
        {summary.plan && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Upcoming invoice estimate</CardTitle>
              <CardDescription>Base subscription plus any usage overages</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{summary.plan.name} plan</span>
                  <span className="tabular-nums">${(summary.plan.monthly_price_cents / 100).toFixed(2)}</span>
                </div>
                {totalOverageCents > 0 && (
                  <div className="flex justify-between text-amber-600 dark:text-amber-400">
                    <span>Usage overages</span>
                    <span className="tabular-nums">+${(totalOverageCents / 100).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold pt-2 border-t border-border">
                  <span>Estimated total</span>
                  <span className="tabular-nums">${(summary.estimated_total_cents / 100).toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment method */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-muted-foreground" />
              Payment method
            </CardTitle>
          </CardHeader>
          <CardContent>
            {card ? (
              <div className="flex items-center gap-3">
                <div className="w-10 h-7 rounded border border-border bg-muted flex items-center justify-center">
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium capitalize">{card.brand ?? 'Card'} ending in {card.last4}</p>
                  <p className="text-xs text-muted-foreground">Expires {card.exp_month}/{card.exp_year}</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground mb-3">No payment method on file.</p>
                <p className="text-xs text-muted-foreground/60">
                  Contact your Ivrello administrator to add a payment method.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
